import React from 'react';
import { Icon, Button, DateRangePicker } from '../../DesignSystem';
import './DashboardFilters.css';

export interface DashboardFiltersState {
  searchText: string;
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  showCancelled: boolean;
}

export const defaultFilters: DashboardFiltersState = {
  searchText: '',
  dateRange: { start: null, end: null },
  showCancelled: true,
};

interface DashboardFiltersProps {
  filters: DashboardFiltersState;
  onFiltersChange: (filters: DashboardFiltersState) => void;
  activeTab: 'active' | 'draft' | 'past';
}

export const hasActiveFilters = (filters: DashboardFiltersState, activeTab: string): boolean => {
  const hasSearch = filters.searchText !== '';
  const hasDateRange = activeTab === 'past' && (filters.dateRange.start !== null || filters.dateRange.end !== null);
  return hasSearch || hasDateRange;
};

const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  filters,
  onFiltersChange,
  activeTab,
}) => {
  const isActive = hasActiveFilters(filters, activeTab);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      searchText: e.target.value
    });
  };

  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    onFiltersChange({
      ...filters,
      dateRange: { start, end }
    });
  };

  const handleClearFilters = () => {
    onFiltersChange(defaultFilters);
  };

  return (
    <div className="dashboard-filters">
      <div className="dashboard-filters__row">
        {/* Search input */}
        <div className="dashboard-filters__search">
          <Icon name="Search" size={16} className="dashboard-filters__search-icon" />
          <input
            type="text"
            placeholder="Search by title..."
            value={filters.searchText}
            onChange={handleSearchChange}
            className="dashboard-filters__search-input"
          />
          {filters.searchText && (
            <button
              type="button"
              className="dashboard-filters__search-clear"
              onClick={() => onFiltersChange({ ...filters, searchText: '' })}
              aria-label="Clear search"
            >
              <Icon name="X" size={14} />
            </button>
          )}
        </div>

        {/* Date range picker - only in Past tab */}
        {activeTab === 'past' && (
          <DateRangePicker
            startDate={filters.dateRange.start}
            endDate={filters.dateRange.end}
            onChange={handleDateRangeChange}
            placeholder="Filter by date..."
            className="dashboard-filters__date-picker"
          />
        )}

        {/* Clear all button */}
        {isActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="dashboard-filters__clear-btn"
          >
            <Icon name="X" size={14} />
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};

export default DashboardFilters;
